// src/styles/globalStyles.js
import { createGlobalStyle } from 'styled-components';

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 244, g: 223, b: 38 }; // Default yellow
};

const GlobalStyles = createGlobalStyle`
  :root {
    /* Dynamically update CSS variables based on selected theme */
    ${props => {
      const primaryColor = props.theme.colors.primary || props.theme.colorTheme?.primary?.main || '#f4df26';
      const primaryDark = props.theme.colors.primaryDark || props.theme.colorTheme?.primary?.dark || '#d4bf1e';
      const primaryLight = props.theme.colors.primaryLight || props.theme.colorTheme?.primary?.light || '#f6e84d';
      const rgb = hexToRgb(primaryColor);
      const rgbDark = hexToRgb(primaryDark);
      
      return `
        --primary-color: ${primaryColor};
        --primary-dark: ${primaryDark};
        --primary-light: ${primaryLight};
        --primary-on-light: ${primaryDark};
        --primary-rgb: ${rgb.r}, ${rgb.g}, ${rgb.b};
        --primary-dark-rgb: ${rgbDark.r}, ${rgbDark.g}, ${rgbDark.b};
        --primary-rgba-05: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05);
        --primary-rgba-10: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1);
        --primary-rgba-15: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15);
        --primary-rgba-20: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2);
        --primary-rgba-25: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25);
        --primary-rgba-30: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3);
        --primary-rgba-40: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4);
        --primary-rgba-50: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5);
        --primary-rgba-60: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6);
        --primary-rgba-90: rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9);
        --secondary-color: ${props.theme.colors.secondary || props.theme.colorTheme?.secondary?.main || '#0ea5e9'};
        --accent-color: ${props.theme.colors.accent || props.theme.colorTheme?.secondary?.main || '#8b5cf6'};
        --success-color: ${props.theme.colors.success || props.theme.colorTheme?.success?.main || '#28a745'};
        --danger-color: ${props.theme.colors.danger || props.theme.colorTheme?.danger?.main || '#dc3545'};
        --warning-color: ${props.theme.colors.warning || props.theme.colorTheme?.warning?.main || '#f4df26'};
      `;
    }}
  }
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: ${props => props.theme.typography.fontFamily};
    background-color: ${props => props.theme.colors.background.main};
    color: ${props => props.theme.colors.text.primary};
    transition: background-color ${props => props.theme.transitions.medium}, color ${props => props.theme.transitions.medium};
    line-height: 1.5;
    position: relative;
    overflow-x: hidden;
    min-height: 100vh;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    margin-bottom: 0.75rem;
    line-height: 1.2;
  }
  
  h1 {
    font-size: ${props => props.theme.typography.h1.fontSize};
    margin-bottom: 1rem;
  }
  
  h2 {
    font-size: ${props => props.theme.typography.h2.fontSize};
    margin-bottom: 0.875rem;
  }
  
  h3 {
    font-size: ${props => props.theme.typography.h3.fontSize};
    margin-bottom: 0.75rem;
  }
  
  h4 {
    font-size: ${props => props.theme.typography.h4.fontSize};
    margin-bottom: 0.625rem;
  }
  
  h5 {
    font-size: ${props => props.theme.typography.h5.fontSize};
    margin-bottom: 0.5rem;
  }
  
  h6 {
    font-size: ${props => props.theme.typography.h6.fontSize};
    margin-bottom: 0.5rem;
  }
  
  p {
    margin-bottom: 0.875rem;
    line-height: 1.6;
  }
  
  small {
    font-size: ${props => props.theme.typography.small?.fontSize || '0.75rem'};
  }
  
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      color: ${props => {
        const hexColor = props.theme.colors.primary.replace('#', '');
        const r = parseInt(hexColor.substring(0, 2), 16);
        const g = parseInt(hexColor.substring(2, 4), 16);
        const b = parseInt(hexColor.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 0.8)`;
      }};
      // filter: drop-shadow(0 0 10px rgba(102, 126, 234, 0.5));
      transform: translateY(-2px);
    }
    
    &:active {
      filter: drop-shadow(0 0 15px rgba(102, 126, 234, 0.7));
    }
  }
  
  button, input, select, textarea {
    font-family: inherit;
    font-size: inherit;
  }
  
  button {
    min-height: 2.5rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    cursor: pointer;
    border: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    
    /* Get primary color from theme for glow */
    --primary-glow: ${props => {
      const primaryColor = props.theme?.colorTheme?.primary?.main || props.theme?.colors?.primary || '#667eea';
      // Convert hex to rgba
      if (primaryColor.startsWith('#')) {
        const hex = primaryColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 0.6)`;
      }
      return 'rgba(102, 126, 234, 0.6)';
    }};
    
    &:hover:not(:disabled) {
      filter: drop-shadow(0 0 15px var(--primary-glow));
      transform: translateY(-2px);
      box-shadow: 0 4px 12px var(--primary-glow);
    }
    
    &:active:not(:disabled) {
      filter: drop-shadow(0 0 20px ${props => {
        const primaryColor = props.theme?.colorTheme?.primary?.main || props.theme?.colors?.primary || '#667eea';
        if (primaryColor.startsWith('#')) {
          const hex = primaryColor.replace('#', '');
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          return `rgba(${r}, ${g}, ${b}, 0.8)`;
        }
        return 'rgba(102, 126, 234, 0.8)';
      }});
      transform: translateY(0);
      box-shadow: 0 2px 8px var(--primary-glow);
    }
    
    /* Touch devices - glow on touch */
    @media (hover: none) and (pointer: coarse) {
      &:active:not(:disabled) {
        filter: drop-shadow(0 0 20px ${props => {
          const primaryColor = props.theme?.colorTheme?.primary?.main || props.theme?.colors?.primary || '#667eea';
          if (primaryColor.startsWith('#')) {
            const hex = primaryColor.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return `rgba(${r}, ${g}, ${b}, 0.8)`;
          }
          return 'rgba(102, 126, 234, 0.8)';
        }});
        box-shadow: 0 2px 8px var(--primary-glow);
      }
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      filter: none;
    }
  }
  
  input, select, textarea {
    min-height: 2.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid ${props => props.theme.colors.background.main};
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
    }
  }
  
  /* Custom scrollbar with responsive sizing */
  ::-webkit-scrollbar {
    width: 0.5rem;
    height: 0.5rem;
  }
  
  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background.main};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.primary}80;
    border-radius: 0.25rem;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary};
  }
  
  /* Transitions for dark/light mode */
  .theme-transition {
    transition: background-color ${props => props.theme.transitions.medium},
                color ${props => props.theme.transitions.medium},
                border-color ${props => props.theme.transitions.medium},
                box-shadow ${props => props.theme.transitions.medium};
  }
  
  /* Spin animation for refresh icons */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  /* Page transitions */
  .page-transition-enter {
    opacity: 0;
    transform: translateY(20px);
  }
  
  .page-transition-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 300ms, transform 300ms;
  }
  
  .page-transition-exit {
    opacity: 1;
    transform: translateY(0);
  }
  
  .page-transition-exit-active {
    opacity: 0;
    transform: translateY(-20px);
    transition: opacity 300ms, transform 300ms;
  }
  
  /* Fullscreen mode - ensure background is visible */
  :fullscreen {
    background-color: ${props => props.theme.colors.background.main};
  }
  
  :-webkit-full-screen {
    background-color: ${props => props.theme.colors.background.main};
  }
  
  :-moz-full-screen {
    background-color: ${props => props.theme.colors.background.main};
  }
  
  :-ms-fullscreen {
    background-color: ${props => props.theme.colors.background.main};
  }
  
  /* Fullscreen backdrop */
  ::backdrop {
    background-color: ${props => props.theme.colors.background.main};
  }
`;

export default GlobalStyles;